import React, { useMemo, useEffect, useState } from 'react';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { GroupByFieldType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/stores/variationsAnalyticStore';
import { BarChartData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { VariationsByCategoryAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsByCategoryAdapter';
import { SeriesColumnOptions } from 'highcharts';

interface QueryFnMap {
  [groupById: string]: () => Promise<VariationsByCategoryAdapter>;
}

interface VariationsParentPageCountByCategoryBarChartProps {
  groupByFields: Array<{ id: string; label: string }>;
  queryFnMap: QueryFnMap;
  renderSubtitle: (selectedGroupBy: string, data?: BarChartData) => string | React.ReactNode;
}

export const VariationsParentPageCountByCategoryBarChart = (
  props: VariationsParentPageCountByCategoryBarChartProps
) => {
  const { groupByFields, queryFnMap, renderSubtitle } = props;
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByFieldType>(GroupByFieldType.Category);

  const currentGroupById = selectedGroupBy;

  const currentQueryFn = useMemo(() => {
    return queryFnMap[currentGroupById] || (() => Promise.resolve([]));
  }, [queryFnMap, currentGroupById]);

  const handleGroupByChange = (option: { id: string }) => {
    setSelectedGroupBy(option.id as GroupByFieldType);
  };

  useEffect(() => {
    setSelectedGroupBy(groupByFields[0].id as GroupByFieldType);
  }, [groupByFields]);

  return (
    <BeaconProChart
      name={`variations-parent-page-count-by-category-bar-chart-${currentGroupById}`}
      queryFn={currentQueryFn}
    >
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <Text variant="h4">Parent Page Count</Text>
          <Text variant="h4">by</Text>
          {groupByFields.length > 1 ? (
            <SlDropdownMenu
              options={groupByFields.map((groupBy) => ({
                id: groupBy.id,
                label: groupBy.label
              }))}
              buttonVariant="title"
              defaultLabel=""
              onChange={handleGroupByChange}
              selectedId={currentGroupById}
            />
          ) : (
            <Text variant="h4">{groupByFields[0]?.label || ''}</Text>
          )}
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(adapter: ChartDataAdapter<BarChartData>) => {
          return renderSubtitle(currentGroupById, adapter.transform());
        }}
      />
      <BeaconProChart.Legend
        primaryLegendProps={{ displayName: mainTimePeriod.displayName }}
        comparisonLegendProps={{ displayName: comparisonTimePeriod.displayName }}
      />
      <BeaconProChart.Visualization
        render={(data: VariationsByCategoryAdapter) => {
          const { chartData } = data.transform();
          return <TopEntityChartV2 chartSeries={chartData as SeriesColumnOptions[]} />;
        }}
      />
    </BeaconProChart>
  );
};
